import React, { useCallback } from "react";
import {
  Popover,
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import moment from "moment";
import clsx from "clsx";
import { readNotification } from "../../services/dashBoardServices";
import { useDispatch } from "react-redux";

interface NotificationPopoverProps {
  anchorEl: null | HTMLElement;
  onClose: () => void;
  notifications: any;
  handleScroll: any;
  setUnreadCount?: React.Dispatch<React.SetStateAction<any>>; // Add this prop
}

const formatTime = (timestamp: string) => moment(timestamp).fromNow();
const NotificationPopover: React.FC<NotificationPopoverProps> = ({
  anchorEl,
  onClose,
  notifications,
  handleScroll,
  setUnreadCount
}) => {
  const [tabValue, setTabValue] = React.useState("1");
  const open = Boolean(anchorEl);
  // console.log(anchorEl,"notifyAnchorEl>>>>");
  // console.log(notifications,"notifications>>>>");
// Normalize notifications data structure
const normalizeNotifications = (data: any) => {
  if (!data) return [];
  // If data is the full response object with count/unreadCount/notifications
  if (data.notifications) {
    // Handle case where notifications is an array of arrays
    if (Array.isArray(data.notifications) && data.notifications.length > 0) {
      // If first item is an array (nested), use that as the main array
      if (Array.isArray(data.notifications[0])) {
        return data.notifications[0]; // Just take the first array
      }
      return data.notifications;
    }
    return [];
  }
  
  // If data is just an array (raw notifications array)
  if (Array.isArray(data)) {
    // If first item is an array (nested), use that
    if (data.length > 0 && Array.isArray(data[0])) {
      return data[0]; // Just take the first array
    }
    return data;
  }
  // If single notification
  if (data._id) {
    return [data];
  }
  return [];
};

// Get all notifications (normalized)
const allNotifications = normalizeNotifications(notifications);
  const unreadNotifications = allNotifications && allNotifications.filter((n: any) => !n.isRead);
  const displayedList = tabValue === "1" ? allNotifications : unreadNotifications;
   const handleReadNotification = useCallback(async (id:any) => {
     try {
      let payload={
        isRead: true
      }
       const response = await readNotification(id,payload);
          // Update the local state with the response data
          if (setUnreadCount && response.data) {
            setUnreadCount((prev: any) => {
              // Create a map to deduplicate notifications by _id
              const notificationMap = new Map();
              
              // Add all existing notifications to the map
              prev.notifications.forEach((n: any) => notificationMap.set(n._id, n));
              
              // Update the specific notification
              notificationMap.set(response.data._id, {
                ...(notificationMap.get(response.data._id) || {}),
                ...response.data,
                isRead: true
              });
      
              return {
                count: response.count ?? prev.count,
                unreadCount: response.unreadCount ?? prev.unreadCount,
                notifications: Array.from(notificationMap.values())
              };
            });
          }
      //  dispatch(setNotificationStatus(true))
     } catch (err) {
       console.error(err);
     }
   }, [setUnreadCount]);
  //  console.log(displayedList,"displayedList>>>>");
   
  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        className: "w-[360px] max-h-[80vh] overflow-y-auto shadow-xl rounded-lg",
      }}
    >
      <Box className="p-4 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
       <p className="font-medium !text-[1rem]">Notification</p>
          {/* <span className="text-sm text-blue-600 cursor-pointer hover:underline">
            Mark All Read
          </span> */}
        </div>
        <Divider />

        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={(_, val) => setTabValue(val)}
          variant="fullWidth"
          className="mt-2"
        >
          <Tab label={`All (${allNotifications.length})`} value="1" />
          <Tab label={`Unread (${unreadNotifications.length})`} value="2" />
        </Tabs>

        {/* Content */}
        <div className="mt-2 max-h-[60vh] overflow-y-auto scrollbar-hidden" onScroll={handleScroll}>
          {displayedList.length > 0 ? (
             [...displayedList]
             .sort((a: any, b: any) => 
               new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
             ).map((item: any,index:number) => (
              <div
                key={index}
                className="flex justify-between items-start p-3 hover:bg-gray-100 rounded cursor-pointer"
                onClick={() => handleReadNotification(item._id)}
              >
                <div className="flex gap-3">
                  <Avatar sx={{ width: 32, height: 32 }}>
                    {item.title?.charAt(0) || "N"}
                  </Avatar>
                  <div className="text-sm text-gray-700">
                    <p className="font-semibold">{item?.title}</p>
                    <p className="text-xs text-gray-500">{item?.name}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span
                    className={clsx(
                      "w-2 h-2 rounded-full",
                      item.isRead ? "bg-gray-300" : "bg-blue-500"
                    )}
                  ></span>
                  <span className="text-xs text-gray-400">
                    {formatTime(item.createdAt)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6 text-gray-400">
              No {tabValue === "2" ? "unread " : ""}notifications
            </div>
          )}
        </div>
      </Box>
    </Popover>
  );
};


export default NotificationPopover;
